import { BodyCopy } from "./BodyCopy";
import { Button } from "lola-framework-ui-test";
import { useTranslation } from "react-i18next";
import { LimitInfoIcon } from "../assets/icons";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { amountFormatter } from "../utils";
import { accentInkOnSurface } from "../utils/accentInkOnSurface";

export interface AmountOutOfRangeInfoProps {
  kind: "below_min" | "above_max";
  method: string;
  bound: number;
  currency: string;
  /** Pinned amount (fund-request): change method. Default: change amount. */
  cta?: "gotIt" | "changeAmount" | "changeMethod";
  onClose: () => void;
}

/**
 * Continuar gate when the send amount is outside the funding-method min/max.
 * Does not clamp the input; the user dismisses and changes amount or method.
 */
export const AmountOutOfRangeInfo: React.FC<AmountOutOfRangeInfoProps> = ({
  kind,
  method,
  bound,
  currency,
  cta = "changeAmount",
  onClose,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn, textColorBtn, iconColors, background } =
    colorConfig;
  const partnerColors = useSessionStore(
    (s) => s.session.data?.partner?.theme?.colors,
  ) as Record<string, string> | undefined;
  // Plain CSS color: the heading cannot take the mesh string.
  const accentSolid = accentInkOnSurface(partnerColors, background, bodyCopy);

  const number = amountFormatter(bound, currency, {
    minimumFractionDigits: Number.isInteger(bound) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(bound) ? 0 : 2,
  });
  const formatted = currency.toUpperCase() === "USD" ? `$${number}` : number;

  const message =
    kind === "above_max"
      ? t("amountLimit.aboveMax", { max: formatted, method, currency })
      : t("amountLimit.belowMin", { min: formatted, method, currency });

  return (
    <section className="amount-limit-slot">
      <LimitInfoIcon size={60} colors={iconColors} />
      <h2 className="amount-limit-heading" style={{ color: accentSolid }}>
        {t("amountLimit.title")}
      </h2>
      <BodyCopy className="amount-limit-text" textColor={bodyCopy}>
        {message}
      </BodyCopy>
      <Button
        size="large"
        background={backgroundBtn}
        color={textColorBtn}
        onClick={onClose}
      >
        {t(
          cta === "changeMethod"
            ? "amountLimit.changeMethod"
            : cta === "gotIt"
              ? "amountLimit.gotIt"
              : "amountLimit.changeAmount",
        )}
      </Button>
    </section>
  );
};
